import { JSXElement, mergeProps } from 'solid-js';
import {
  ActionsDropdown,
  type ActionMenuItem
} from '~/components/ActionsDropdown';
import { Localized } from '~/i18n';
import { getTranslationKey } from './utils';
import { ApiConfigData, EApplicationType } from '~/api';

export type ApplicationActionsDropdownProps = {
  appData: ApiConfigData;
  dropdownId: string;
  showEdit?: boolean;
  onEdit?: (appData: ApiConfigData) => void;
  onDelete?: (appData: ApiConfigData) => void | Promise<unknown>;
  deleteLabelKey?: string;
  renderTrigger?: (isOpen: boolean) => JSXElement;
  dataTestIdPrefix?: string;
};

export default function ApplicationActionsDropdown(
  props: ApplicationActionsDropdownProps
) {
  const merged = mergeProps(
    { deleteLabelKey: 'cta_delete_application' as const, showEdit: false },
    props
  );

  const isDefaultApp =
    (merged.appData.clientType as EApplicationType) ===
    EApplicationType.DEFAULT_APPLICATION;

  const prefix = merged.dataTestIdPrefix ?? merged.dropdownId;

  const items: ActionMenuItem[] = [
    ...(merged.showEdit && merged.onEdit
      ? [
          {
            label: <Localized translationKey={getTranslationKey('cta_edit')} />,
            onClick: () => merged.onEdit!(merged.appData),
            dataTestId: `${prefix}-edit-${merged.appData.keyId}`
          }
        ]
      : []),
    ...(!isDefaultApp && merged.onDelete
      ? [
          {
            label: (
              <span class="text-error">
                <Localized
                  translationKey={getTranslationKey(merged.deleteLabelKey)}
                />
              </span>
            ),
            onClick: () => merged.onDelete!(merged.appData),
            dataTestId: `${prefix}-delete-${merged.appData.keyId}`
          }
        ]
      : [])
  ];

  return (
    <ActionsDropdown
      id={merged.dropdownId}
      items={items}
      {...(merged.renderTrigger && {
        children: merged.renderTrigger
      })}
    />
  );
}
